import { useState } from "react";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AddressForm } from "./AddressForm";
import type { EnderecoApi } from "../types";
import type { AddressFormData } from "../schemas/personSchema";

interface AddressTableProps {
  addresses: EnderecoApi[];
  personId?: string;
  onAdd: (data: AddressFormData) => void;
  onUpdate: (id: number, data: AddressFormData) => void;
  onDelete: (id: number) => void;
  isLoading?: boolean;
}

const addressTypeLabels: Record<string, string> = {
  residencial: "Residencial",
  comercial:   "Comercial",
  cobranca:    "Cobrança",
  entrega:     "Entrega",
  outro:       "Outro",
};

export const AddressTable = ({
  addresses,
  personId,
  onAdd,
  onUpdate,
  onDelete,
  isLoading,
}: AddressTableProps) => {
  const [isFormOpen, setIsFormOpen]       = useState(false);
  const [editingAddress, setEditingAddress] = useState<EnderecoApi | null>(null);
  const [deletingId, setDeletingId]       = useState<number | null>(null);

  const handleAdd = (data: AddressFormData) => {
    onAdd(data);
    setIsFormOpen(false);
  };

  const handleUpdate = (data: AddressFormData) => {
    if (editingAddress) {
      onUpdate(editingAddress.id, data);
      setEditingAddress(null);
    }
  };

  const handleDelete = () => {
    if (deletingId !== null) {
      onDelete(deletingId);
      setDeletingId(null);
    }
  };

  const formatAddress = (address: EnderecoApi) => {
    const parts = [
      address.logradouro,
      address.numero,
      address.complemento,
      address.bairro,
    ].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Endereços
        </h3>
        <Button onClick={() => setIsFormOpen(true)} size="sm" disabled={!personId}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Endereço
        </Button>
      </div>

      {!personId && (
        <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
          Salve a pessoa primeiro para adicionar endereços
        </p>
      )}

      {personId && addresses.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
          Nenhum endereço cadastrado
        </p>
      )}

      {personId && addresses.length > 0 && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Endereço</TableHead>
                <TableHead>Cidade/UF</TableHead>
                <TableHead>CEP</TableHead>
                <TableHead>Distância</TableHead>
                <TableHead>Tempo Entrega</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {addresses.map((address) => (
                <TableRow key={address.id}>
                  <TableCell>
                    <Badge variant="outline">
                      {addressTypeLabels[address.tipo] ?? address.tipo}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[250px] truncate">
                    {formatAddress(address)}
                  </TableCell>
                  <TableCell>
                    {address.cidade}/{address.uf}
                  </TableCell>
                  <TableCell>{address.cep || "-"}</TableCell>
                  <TableCell>{address.distancia || "-"}</TableCell>
                  <TableCell>{address.tempoEntrega || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingAddress(address)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingId(address.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog para adicionar */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Endereço</DialogTitle>
          </DialogHeader>
          <AddressForm
            onSubmit={handleAdd}
            onCancel={() => setIsFormOpen(false)}
            isLoading={isLoading}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog para editar */}
      <Dialog open={!!editingAddress} onOpenChange={(open) => !open && setEditingAddress(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Endereço</DialogTitle>
          </DialogHeader>
          {editingAddress && (
            <AddressForm
              onSubmit={handleUpdate}
              onCancel={() => setEditingAddress(null)}
              defaultValues={editingAddress}
              isLoading={isLoading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
